import { CToken } from "@models/configurators/ctoken";
import { KbObject } from "@models/configurators/kb-object";
import { KbObjectManager } from "@models/configurators/kb-object-manager";
import { OutputField } from "@models/configurators/output-fields/output-field";
import { Output } from "@models/configurators/outputs/output";
import { IOutput, IOutputField } from "@models/entity-models";
import { eOutputFieldType, eOutputType } from "@models/enums";
import { Constructor, IMap } from "@tools";

export interface IKbo {
    token: CToken;
    class: Constructor<KbObject>;
    outputFieldType?: eOutputFieldType;
    outputTypes?: eOutputType[];
}

export function Kbo(token: CToken) {
    return (target: Constructor<any>) => {
        ConfiguratorDI.register({ token, class: target });
    };
}

export function KboOutputField(token: CToken, outputFieldType: eOutputFieldType) {
    return (target: Constructor<any>) => {
        ConfiguratorDI.register({ token, class: target, outputFieldType });
    };
}

export function KboOutput(token: CToken, ...outputTypes: eOutputType[]) {
    return (target: Constructor<any>) => {
        ConfiguratorDI.register({ token, class: target, outputTypes });
    };
}

/**
 * Applied to a getter or setter property, will make it enumerable
 */
export function Enumerable() {
    return function (target: any, propertyKey: string, descriptor: PropertyDescriptor) {
        descriptor.enumerable = true;
    }
}

export class ConfiguratorDI {
    private static db: IMap<IKbo> = {};

    public static register(args: IKbo) {
        if (ConfiguratorDI.db.hasOwnProperty(args.token.key)) {
            throw new Error(`A type with key ${args.token.key} has already been registered`);
        }
        ConfiguratorDI.db[args.token.key] = args;

        args.class.prototype.$type = args.token.key;
        if (args.outputFieldType) args.class.prototype.type = args.outputFieldType;
        if (args.outputTypes && args.outputTypes.length == 1) args.class.prototype.type = args.outputTypes[0];
    }

    public static get(token: CToken): IKbo {
        return ConfiguratorDI.db[token.key];
    }

    /**
     * Checks if the instance of a class given is an instance of the class represented by it's 
     * injectable class key.  If the instance is an instance of a class that was not
     * decorated, or if the class represented by the key was not decorated, then this will
     * return false!
     * @param instance An instance of a class that was decorated
     * @param classKey String key of the class you want to see if the instance is an instance of
     */
    public static instanceOf<T>(instance: any, token: CToken): instance is T {
        // find the injectable
        let inj = this.get(token);
        return inj ? instance instanceof inj.class : false;
    }

    public static getNewOutputField<T extends OutputField>(
        fieldType: eOutputFieldType,
        kom: KbObjectManager,
        args?: IOutputField
    ): T {
        let ikbo: IKbo = Object.values(ConfiguratorDI.db).find((k: IKbo) => k.outputFieldType == fieldType);
        if (!ikbo) throw new Error(`Cannot not find output field type '${fieldType}`);
        let f = new ikbo.class(kom) as T;
        if (args) {
            kom.recurse(args, f);
        }
        return f;
    }

    public static getNewOutput<T extends Output>(outputType: eOutputType, kom: KbObjectManager, args?: IOutput): T {
        let ikbo: IKbo = Object.values(ConfiguratorDI.db).find((k: IKbo) => k.outputTypes && k.outputTypes.contains(outputType));
        if (!ikbo) throw new Error(`Cannot not find output type '${outputType}`);

        let o = new ikbo.class(kom) as T;
        if (!o.type) {
            o.type = outputType;
        }
        if (args) {
            kom.recurse(args, o);
        }
        o.name = (args && args.name) ? args.name : loc["eoutputtype_" + outputType.toLowerCase() + "_title"];
        return o;
    }

    public static getOutputFieldClassName(outputFieldType: eOutputFieldType) {
        let ikbo: IKbo = Object.values(ConfiguratorDI.db).find((k: IKbo) => k.outputFieldType == outputFieldType);
        return ikbo.token.key;
    }

    public static getOutputClassName(outputType: eOutputType) {
        let ikbo: IKbo = Object.values(ConfiguratorDI.db).find((k: IKbo) => k.outputTypes &&  k.outputTypes.contains(outputType));
        return ikbo.token.key;
    }
}
